/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import MobiusLink from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AwesomePhone from "@us/Assets/Icons/AwesomePhone";
import CrossRefArrow from "@us/Assets/Icons/CrossRefArrow";
import Lock from "@us/Assets/Icons/Lock";
import SupportIcon from "@us/Assets/Icons/SupportIcon";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    destination = "destination",
    overrideTitle = "overrideTitle",
    customCSS = "customCSS",
    showIcon = "showIcon",
    icon = "icon",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.destination]: LinkFieldValue;
        [fields.overrideTitle]: string;
        [fields.customCSS]: string;
        [fields.showIcon]: boolean;
        [fields.icon]: string;
    };
}
export interface LinkStyles {
    linkWrapper?: InjectableCss;
    titleIcon?: IconPresentationProps;
}
const linkStyles: LinkStyles = {
    linkWrapper: {
        css: css`
            font-size: 12px;
            display: flex;
            a span[class^="TypographyStyle"] {
                color: #fff;
                font-weight: normal;
            }
        `,
    },
    titleIcon: {
        css: css`
            margin-right: 5px;
        `,
    },
};
let icon: React.ComponentType<IconPresentationProps>;
export const Link: FC<OwnProps> = ({ fields }) => {
    const { title, url } = useGetLink(fields.destination);

    switch (fields.icon) {
        case "Lock":
            icon = Lock;
            break;
        case "Phone":
            icon = AwesomePhone;
            break;
        case "CrossRefArrow":
            icon = CrossRefArrow;
            break;
        case "Support":
            icon = SupportIcon;
            break;
    }

    const styles = useMergeStyles("link", linkStyles);

    if (!url) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper {...styles.linkWrapper}>
                {fields.showIcon && <Icon {...styles.titleIcon} src={icon} size={18} />}
                <MobiusLink className="link" {...styles} href={url}>
                    {fields.overrideTitle || title || url}
                </MobiusLink>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.link {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Link,
    definition: {
        group: "Basic",
        icon: "link",
        fieldDefinitions: [
            {
                name: fields.destination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Contextual",
                isRequired: true,
                tab: basicTab,
            },
            {
                name: fields.overrideTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.showIcon,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.icon,
                displayName: "Link Icon",
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showIcon,
                options: [
                    {
                        displayName: "Cross Reference Arrow",
                        value: "CrossRefArrow",
                    },
                    {
                        displayName: "Phone",
                        value: "Phone",
                    },
                    {
                        displayName: "Lock",
                        value: "Lock",
                    },
                    {
                        displayName: "Support",
                        value: "Support"
                    }
                ],
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
